import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'
import { DocStep } from '../shared/DocStep'

const Monetization = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Guild&apos;s monetization features let Organizers accept payments
            for Event tickets and collect donations from their community. All
            payments are processed through Stripe, a trusted payment platform
            used by millions of businesses. Connecting Stripe is required before
            you can create paid Events or accept donations.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/guilds/guild-settings-monetization.png'
          alt={t`Guild Monetization settings page showing Connect Stripe button to enable payments for Event tickets and donations`}
          caption={t`Connect Stripe from the Monetization settings to accept payments`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='connecting-stripe' title={t`Connecting Stripe`}>
          <Paragraph size='$5'>
            <Trans>
              Only <Link href='/docs/guilds/members'>Organizers</Link> can
              connect Stripe to a Guild.
            </Trans>
          </Paragraph>
          <DocStep
            number={1}
            title={t`Open Guild Settings`}
            description={t`Navigate to your Guild's profile page and click the settings gear icon.`}
          />
          <DocStep
            number={2}
            title={t`Go to the Monetization section`}
            description={t`In Settings, navigate to the Monetization section.`}
          />
          <DocStep
            number={3}
            title={t`Click Connect Stripe`}
            description={t`Click the Connect Stripe button. You'll be redirected to Stripe's onboarding flow where you can create a new Stripe account or connect an existing one.`}
          />
          <DocStep
            number={4}
            title={t`Complete Stripe onboarding`}
            description={t`Follow Stripe's steps to verify your identity and set up your payout details. Once complete, you'll be redirected back to Guild.`}
          />
          <Paragraph size='$5'>
            <Trans>
              Once connected, your Guild can create paid{' '}
              <Link href='/docs/events/ticketing-overview'>Event tickets</Link>{' '}
              and accept donations. Stripe handles payment processing and
              payouts. Guild lets Organizers configure ticket tax fields where
              needed, but tax rules vary by location, so confirm your local
              requirements before selling tickets.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='sponsorships' title={t`Sponsorships & Analytics`}>
          <Paragraph size='$5'>
            <Trans>
              Guild Settings also includes a Sponsorship area for communities
              that display sponsors across Guild pages, Events, Presentations,
              and email. Organizers can create sponsorship cards with light and
              dark images, a description, link text, and separate web and email
              destination URLs.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              The Sponsorship dashboard tracks web views, email views, web
              clicks, email clicks, click-through rate, geographic performance,
              and per-sponsor comparison over the selected date range. You can
              also request a metrics export for reporting.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              Sponsorships are managed separately from Stripe ticketing and
              donations. Use Monetization for payments and Sponsorship for
              sponsor placement, display, and analytics.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='event-ticketing' title={t`Event Ticketing`}>
          <Paragraph size='$5'>
            <Trans>
              With Stripe connected, you can create paid ticket tiers for your
              Events. This lets you charge for attendance, offer different
              pricing levels (such as early bird, general admission, or VIP),
              and manage capacity for each tier individually.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              For a complete guide to setting up ticketing — including creating
              tiers, setting prices, managing capacity, and checkout — see{' '}
              <Link href='/docs/events/ticketing-overview'>
                Ticketing & Payments
              </Link>
              .
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='donations' title={t`Donations`}>
          <Paragraph size='$5'>
            <Trans>
              Guilds with Stripe connected can accept donations from their
              community. Donations allow Members and supporters to contribute
              financially to the Guild without being tied to a specific Event.
              This is useful for communities that want to fund venue costs,
              speaker travel, equipment, or other community expenses.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              When donations are enabled, a donation option is available on the
              Guild&apos;s profile page. Supporters can choose how much to
              contribute, and the payment is processed through your connected
              Stripe account.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='payouts' title={t`Payouts`}>
          <Paragraph size='$5'>
            <Trans>
              All revenue from ticket sales and donations is collected in your
              connected Stripe account. Stripe handles payouts to your bank
              account based on the payout schedule you configured during Stripe
              onboarding. You can view and manage your payout settings,
              transaction history, and financial reports directly in the Stripe
              Dashboard.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              Guild charges a 5% fee on ticket sales and donations, on top of
              Stripe&apos;s standard processing fees.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Monetization
